import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { TierStats } from '../../types';
import { TIER_COLORS, TIER_LABELS } from './tierChartConfig';
import {
  CHART_AXIS_PROPS,
  CHART_AXIS_TICK,
  CHART_BAR_PROPS,
  CHART_GRID_PROPS,
  CHART_TOOLTIP_LABEL_STYLE,
  CHART_TOOLTIP_STYLE,
} from './chartTheme';
import {
  ChartBarGradient,
  ChartEmptyState,
  ChartPanel,
} from './ChartPrimitives';
import { chartGradientFill, useChartGradientId } from './chartGradient';

interface TierAverageCostChartProps {
  stats: TierStats[];
  title?: string;
}

function formatCost(amount: number): string {
  if (amount < 0.01) return `$${amount.toFixed(4)}`;
  if (amount < 1) return `$${amount.toFixed(3)}`;
  return `$${amount.toFixed(2)}`;
}

export function TierAverageCostChart({
  stats,
  title = 'Average Price by Tier',
}: TierAverageCostChartProps) {
  const gradientBaseId = useChartGradientId('tier-cost');
  const data = stats
    .filter((item) => item.completedCount > 0)
    .map((item) => ({
      ...item,
      name: TIER_LABELS[item.tier],
      color: TIER_COLORS[item.tier],
    }));

  return (
    <ChartPanel
      title={title}
      iconClassName="bg-success-50 text-success-600"
      icon={
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      }
      summary={<span className="text-[10px] text-slate-500">Completed scans only</span>}
    >

      {data.length > 0 ? (
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 12, right: 10, left: 0, bottom: 5 }}>
              <defs>
                {Object.entries(TIER_COLORS).map(([tier, color]) => (
                  <ChartBarGradient key={tier} id={`${gradientBaseId}-${tier}`} color={color} />
                ))}
              </defs>
              <CartesianGrid {...CHART_GRID_PROPS} />
              <XAxis
                {...CHART_AXIS_PROPS}
                dataKey="name"
                tick={CHART_AXIS_TICK}
                interval={0}
              />
              <YAxis
                {...CHART_AXIS_PROPS}
                tick={CHART_AXIS_TICK}
                width={52}
                tickFormatter={(value: number) => formatCost(value)}
              />
              <Tooltip
                contentStyle={CHART_TOOLTIP_STYLE}
                labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                formatter={(value: number) => [formatCost(value), 'Average Price']}
                labelFormatter={(label, payload) => {
                  if (payload && payload.length > 0) {
                    const item = payload[0].payload as (typeof data)[number];
                    return `${label} (${item.completedCount} completed scans)`;
                  }
                  return String(label);
                }}
              />
              <Bar
                {...CHART_BAR_PROPS}
                dataKey="averageCost"
                radius={[3, 3, 0, 0]}
                isAnimationActive={false}
              >
                {data.map((item) => (
                  <Cell
                    key={item.tier}
                    fill={chartGradientFill(`${gradientBaseId}-${item.tier}`)}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <ChartEmptyState>
          <div className="text-center">
            <p>No tier cost data available</p>
            <p className="text-sm mt-1">Completed scans are required</p>
          </div>
        </ChartEmptyState>
      )}
    </ChartPanel>
  );
}
